// Scheduled Reports management (Usage & Cost Tracking, Phase 4).
// CRUD against /api/usage/schedules. A schedule emails a PDF to each recipient,
// generated against THAT recipient's own live privileges (server-enforced) —
// recipients must be platform users; the backend rejects unknown emails.
import React, { useCallback, useEffect, useState } from 'react';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { authHeaders } from '../../utils/authHeader';
import './UsageDashboard.css';
import './ScheduledReports.css';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; // Python Mon=0

const EMPTY_FORM = {
  report_type: 'usage',
  project_scope: 'assigned',
  frequencyType: 'daily',
  weekdays: [],
  monthdays: '',
  delivery_time: '06:00',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  recipients: '',
  preset: 'month',
  enabled: true,
};

async function api(method, path, body) {
  const res = await fetch(path, {
    method,
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data?.invalid_recipients
      ? ` (${data.invalid_recipients.join(', ')})` : '';
    throw new Error((data?.error || `HTTP ${res.status}`) + detail);
  }
  return data;
}

function describeFrequency(freq) {
  if (!freq) return '—';
  if (freq.type === 'daily') return 'Every day';
  if (freq.type === 'weekly') {
    return `Weekly: ${(freq.weekdays || []).map((d) => WEEKDAYS[d]).join(', ')}`;
  }
  if (freq.type === 'monthly') {
    return `Monthly: day ${(freq.monthdays || []).join(', ')}`;
  }
  return '—';
}

const ScheduledReports = () => {
  const { permissions, loading: permsLoading } = useUserPermissions();
  const perms = permissions?.permissions || [];
  const canSchedule = perms.includes('schedule_usage_reports');
  const canScheduleCost = perms.includes('schedule_cost_reports');
  const canAllProjects = perms.includes('view_all_projects');

  const [schedules, setSchedules] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api('GET', '/api/usage/schedules');
      setSchedules(data.schedules || []);
    } catch (e) {
      setError(`Failed to load schedules: ${e.message}`);
    }
  }, []);

  useEffect(() => { if (canSchedule) load(); }, [canSchedule, load]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleWeekday = (d) => setForm((f) => ({
    ...f,
    weekdays: f.weekdays.includes(d)
      ? f.weekdays.filter((x) => x !== d)
      : [...f.weekdays, d].sort(),
  }));

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({
      report_type: s.report_type,
      project_scope: s.project_scope,
      frequencyType: s.frequency?.type || 'daily',
      weekdays: s.frequency?.weekdays || [],
      monthdays: (s.frequency?.monthdays || []).join(','),
      delivery_time: s.delivery_time || '06:00',
      timezone: s.timezone || 'UTC',
      recipients: (s.recipients || []).join(', '),
      preset: s.filters?.preset || 'month',
      enabled: s.enabled,
    });
    setNotice('');
    setError('');
  };

  const cancelEdit = () => { setEditingId(null); setForm(EMPTY_FORM); };

  const submit = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const payload = {
        report_type: form.report_type,
        project_scope: form.project_scope,
        filters: { preset: form.preset },
        frequency: {
          type: form.frequencyType,
          weekdays: form.frequencyType === 'weekly' ? form.weekdays : [],
          monthdays: form.frequencyType === 'monthly'
            ? form.monthdays.split(',').map((x) => parseInt(x.trim(), 10)).filter((n) => !Number.isNaN(n))
            : [],
        },
        delivery_time: form.delivery_time,
        timezone: form.timezone,
        recipients: form.recipients.split(',').map((x) => x.trim()).filter(Boolean),
        enabled: form.enabled,
      };
      if (editingId) {
        await api('PUT', `/api/usage/schedules/${editingId}`, payload);
        setNotice('Schedule updated.');
      } else {
        await api('POST', '/api/usage/schedules', payload);
        setNotice('Schedule created.');
      }
      cancelEdit();
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete this ${s.report_type} report schedule?`)) return;
    try {
      await api('DELETE', `/api/usage/schedules/${s.id}`);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  if (permsLoading) {
    return (
      <div className="usage-dashboard">
        <div className="usage-content usage-state">
          <div className="usage-loading">
            <div className="usage-loading-spinner" aria-hidden="true" />
            <span>Loading…</span>
          </div>
        </div>
      </div>
    );
  }
  if (!canSchedule) {
    return (
      <div className="usage-dashboard">
        <div className="usage-content usage-state">
          <div className="usage-denied"><h2>Access Denied</h2>
            <p>You don't have permission to manage scheduled reports.</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="usage-dashboard">
      <main className="usage-content">
      <div className="usage-header"><h1>Scheduled Reports</h1></div>
      {error && <div className="usage-error">{error}</div>}
      {notice && <div className="sched-notice">{notice}</div>}

      <div className="sched-form">
        <h3>{editingId ? 'Edit schedule' : 'New schedule'}</h3>
        <div className="sched-grid">
          <label>Report
            <select value={form.report_type} onChange={(e) => set('report_type', e.target.value)}>
              <option value="usage">Usage</option>
              {canScheduleCost && <option value="cost">Usage &amp; Cost</option>}
            </select>
          </label>
          <label>Data range
            <select value={form.preset} onChange={(e) => set('preset', e.target.value)}>
              <option value="day">Last day</option>
              <option value="week">Last week</option>
              <option value="month">Last month</option>
              <option value="year">Last year</option>
            </select>
          </label>
          {canAllProjects && (
            <label>Projects
              <select value={form.project_scope} onChange={(e) => set('project_scope', e.target.value)}>
                <option value="assigned">Assigned</option>
                <option value="all">All</option>
              </select>
            </label>
          )}
          <label>Frequency
            <select value={form.frequencyType} onChange={(e) => set('frequencyType', e.target.value)}>
              <option value="daily">Every day</option>
              <option value="weekly">Selected weekdays</option>
              <option value="monthly">Selected month days</option>
            </select>
          </label>
          {form.frequencyType === 'weekly' && (
            <div className="sched-weekdays">
              {WEEKDAYS.map((label, d) => (
                <button key={d} type="button"
                  className={form.weekdays.includes(d) ? 'active' : ''}
                  onClick={() => toggleWeekday(d)}>{label}</button>
              ))}
            </div>
          )}
          {form.frequencyType === 'monthly' && (
            <label>Days of month (comma-separated, e.g. 1,15)
              <input type="text" value={form.monthdays}
                onChange={(e) => set('monthdays', e.target.value)} placeholder="1,15" />
            </label>
          )}
          <label>Delivery time
            <input type="time" value={form.delivery_time}
              onChange={(e) => set('delivery_time', e.target.value)} />
          </label>
          <label>Timezone
            <input type="text" value={form.timezone}
              onChange={(e) => set('timezone', e.target.value)} placeholder="UTC" />
          </label>
          <label className="sched-wide">Recipients (platform users, comma-separated)
            <input type="text" value={form.recipients}
              onChange={(e) => set('recipients', e.target.value)}
              placeholder="user1@company.com, user2@company.com" />
          </label>
          <label className="sched-inline">
            <input type="checkbox" checked={form.enabled}
              onChange={(e) => set('enabled', e.target.checked)} /> Enabled
          </label>
        </div>
        <p className="usage-muted sched-note">
          Each recipient receives a PDF scoped to their own current access —
          a recipient without cost access gets the usage version.
        </p>
        <div className="sched-actions">
          <button onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : editingId ? 'Save changes' : 'Create schedule'}
          </button>
          {editingId && <button className="sched-secondary" onClick={cancelEdit}>Cancel</button>}
        </div>
      </div>

      <div className="usage-table-wrap">
        <h3>My schedules</h3>
        <table className="usage-table">
          <thead>
            <tr>
              <th>Report</th><th>Frequency</th><th>Time</th><th>Timezone</th>
              <th>Recipients</th><th>Enabled</th><th>Last sent</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {schedules.map((s) => (
              <tr key={s.id}>
                <td>{s.report_type === 'cost' ? 'Usage & Cost' : 'Usage'}</td>
                <td>{describeFrequency(s.frequency)}</td>
                <td>{s.delivery_time}</td>
                <td>{s.timezone}</td>
                <td>{(s.recipients || []).join(', ')}</td>
                <td>{s.enabled ? 'Yes' : 'No'}</td>
                <td>{s.last_fired_at ? new Date(s.last_fired_at).toLocaleString() : '—'}</td>
                <td>
                  <div className="actions">
                    <button onClick={() => startEdit(s)}>Edit</button>
                    <button className="btn-delete" onClick={() => remove(s)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {schedules.length === 0 && (
              <tr><td colSpan={8} className="usage-empty">No schedules yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      </main>
    </div>
  );
};

export default ScheduledReports;
